import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { api, errMsg, setApiGarage } from "@/lib/api";
import { useAuth, staffMemberships, GARAGE_KEY, homePathFor } from "@/context/AuthContext";
import { LoadingState } from "@/components/common";
import SelectGarage from "@/pages/pro/SelectGarage";
import { GarageError } from "@/components/pro/GarageError";

const GarageCtx = createContext(null);

function initialGarage(isAdmin, staff) {
  const saved = localStorage.getItem(GARAGE_KEY);
  if (isAdmin) return saved;
  if (staff.some((m) => m.garage_id === saved)) return saved;
  return staff.length === 1 ? staff[0].garage_id : null;
}

export function GarageProvider({ children }) {
  const { me } = useAuth();
  const navigate = useNavigate();
  const isAdmin = !!me.user.is_super_admin;
  const staff = staffMemberships(me.memberships);
  const [activeId, setActiveId] = useState(() => initialGarage(isAdmin, staff));
  const [garage, setGarage] = useState(null);
  const [error, setError] = useState(null);
  setApiGarage(activeId);

  const load = useCallback(async () => {
    if (!activeId) return;
    try {
      const { data } = await api.get("/garage");
      setGarage(data);
      setError(null);
    } catch (e) {
      setGarage(null);
      setError(errMsg(e));
    }
  }, [activeId]);

  useEffect(() => { setGarage(null); setError(null); load(); }, [load]);

  const switchGarage = (id) => {
    localStorage.setItem(GARAGE_KEY, id);
    setApiGarage(id);
    setActiveId(id);
    navigate("/pro");
  };

  if (!isAdmin && staff.length === 0) return <Navigate to={homePathFor(me)} replace />;
  if (!activeId) return isAdmin ? <Navigate to="/admin/support" replace /> : <SelectGarage staff={staff} onSelect={switchGarage} />;
  if (error) return <GarageError message={error} isAdmin={isAdmin} staff={staff} onSelect={switchGarage} onRetry={load} />;
  if (!garage) return <LoadingState full />;

  const access = garage.access;
  const value = {
    garage, setGarage, reload: load, activeId, staff, switchGarage, access, isAdmin,
    canManage: access.role === "garage_manager" && !access.read_only,
    isManager: access.role === "garage_manager",
    readOnly: access.read_only,
    features: garage.features || {},
    hasFeature: (k) => Boolean(garage.features?.[k]),
  };
  return <GarageCtx.Provider value={value}>{children}</GarageCtx.Provider>;
}

export const useGarage = () => useContext(GarageCtx);
